import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { placeBet, getBetHistory, requestCashoutQuote, executeCashout, getEvent } from '../api/api';
import { TicketOut, CashoutQuoteOut } from '../api/types';
import { choose, getCombinations, calculateSystemPossibleWin } from '../utils/combinations';
import { buildEventGroups } from '../utils/ticketHelpers';
import { useTranslation } from '../i18n';
import { useLiveScores } from '../hooks/useLiveScores';
import { isOpenForCashout } from '../utils/ticketHelpers';
import { resolveMarketName, resolveOutcomeName, parseSpecifiers, translateOutcomeLabel } from '../utils/marketTranslations';
import { resolveMarketAndOutcome, legStatusIcon, legStatusLabel } from './DesktopMyBetsPage';
import { useBetSlipQuote } from '../hooks/useBetSlipQuote';
export interface BetItem {
  id: string;
  matchId: string | number;
  matchName: string;
  betType: 'home' | 'draw' | 'away';
  tippLabel: string;
  odds: string;
  eventId?: string;
  marketId?: string;
  outcomeId?: string;
  outcomeName?: string;
  specifiers?: string;
  isLocked?: boolean;
  // MK+ / Bet Builder (CustomBet) — selections bundled as operator:"all"
  isCustomBet?: boolean;
  customBetGroupId?: string;
  customBetSelections?: { market_id: string; specifiers: string; outcome_id: string }[];
}

interface BettingSlipDrawerProps {
  bets: BetItem[];
  onRemoveBet: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
  onLoginClick: () => void;
}

const BettingSlipDrawer: React.FC<BettingSlipDrawerProps> = ({
  bets,
  onRemoveBet,
  onClearAll,
  onClose,
  onLoginClick,
}) => {

  const { t: translate, locale } = useTranslation();
  const [drawerTab, setDrawerTab] = useState<'wettschein' | 'offene'>('wettschein');
  const [numpadTarget, setNumpadTarget] = useState<string | null>(null);
  const [tickets, setTickets] = useState<TicketOut[]>([]);
  const [cashoutQuotes, setCashoutQuotes] = useState<Record<string, CashoutQuoteOut>>({});
  const [cashoutLoading, setCashoutLoading] = useState<Record<string, boolean>>({});
  const [cashoutErrors, setCashoutErrors] = useState<Record<string, string>>({});
  const [cashoutConfirming, setCashoutConfirming] = useState<Record<string, boolean>>({});
  const [eventNames, setEventNames] = useState<Record<string, string>>({});
  const liveScores = useLiveScores(tickets);

  const isLoggedIn = !!localStorage.getItem('access_token');


  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (drawerTab === 'offene' && isLoggedIn) {
      const fetchTickets = () => {
        getBetHistory(200).then(setTickets).catch(console.error);
      };
      fetchTickets();
      intervalId = setInterval(fetchTickets, 30000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [drawerTab, isLoggedIn]);

  useEffect(() => {
    if (tickets.length === 0) return;

    const namesFromApi: Record<string, string> = {};
    tickets.forEach(t => t.legs?.forEach(l => {
      if (l.event_name) {
        namesFromApi[l.event_id] = l.event_name;
      }
    }));

    if (Object.keys(namesFromApi).length > 0) {
      setEventNames(prev => ({ ...prev, ...namesFromApi }));
    }
  }, [tickets]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const openTickets = tickets.filter(t => isOpenForCashout(t));
    if (openTickets.length === 0) return;

    const fetchQuotes = async () => {
      openTickets.forEach(t => {
        requestCashoutQuote(t.id).then(quote => {
          setCashoutQuotes(prev => ({ ...prev, [t.id]: quote }));
        }).catch(() => {
          setCashoutQuotes(prev => {
            if (!prev[t.id]) return prev;
            const next = { ...prev };
            delete next[t.id];
            return next;
          });
        });
      });
    };

    fetchQuotes();
    const intervalId = setInterval(fetchQuotes, 5000);
    return () => clearInterval(intervalId);
  }, [tickets, isLoggedIn]);


  const handleConfirmCashout = async (ticketId: string, quoteId: string) => {
    setCashoutLoading(prev => ({ ...prev, [ticketId]: true }));
    setCashoutErrors(prev => ({ ...prev, [ticketId]: '' }));
    try {
      await executeCashout(quoteId);
      setCashoutQuotes(prev => {
        const next = { ...prev };
        delete next[ticketId];
        return next;
      });
      setCashoutConfirming(prev => ({ ...prev, [ticketId]: false }));
      window.dispatchEvent(new Event('balance-changed'));
      getBetHistory(200).then(setTickets).catch(console.error);
    } catch (err: any) {
      setCashoutErrors(prev => ({ ...prev, [ticketId]: err.message || translate('misc.error') }));
    } finally {
      setCashoutLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  const handleCancelCashout = (ticketId: string) => {
    setCashoutConfirming(prev => ({ ...prev, [ticketId]: false }));
    setCashoutErrors(prev => ({ ...prev, [ticketId]: '' }));
  };

  const [stake, setStake] = useState('5');
  const [placing, setPlacing] = useState(false);
  const [betStatus, setBetStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (betStatus === 'success') {
      const timer = setTimeout(() => {
        setBetStatus('idle');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [betStatus]);
  const [betError, setBetError] = useState('');

  const [betslipMode, setBetslipMode] = useState<'single' | 'combi' | 'system'>('combi');
  const [individualStakes, setIndividualStakes] = useState<Record<string, string>>({});

  const handleNumpadPress = (val: string) => {
    const updateVal = (current: string) => {
      if (val === 'DEL') {
        return current.length > 1 ? current.slice(0, -1) : '0';
      } else if (val === ',') {
        return current.includes('.') ? current : current + '.';
      } else {
        return current === '0' || current === '' ? val : current + val;
      }
    };

    if (numpadTarget === 'combi' || numpadTarget === 'system' || numpadTarget === 'all') {
      const newVal = updateVal(stake);
      setStake(newVal);
      if (numpadTarget === 'all') {
        const updated: Record<string, string> = {};
        bets.forEach(b => { updated[b.id] = newVal; });
        setIndividualStakes(updated);
      }
    } else if (numpadTarget) {
      const current = individualStakes[numpadTarget] || '0';
      setIndividualStakes({ ...individualStakes, [numpadTarget]: updateVal(current) });
    }
  };

  const currentNumpadValue = numpadTarget === 'combi' || numpadTarget === 'system' || numpadTarget === 'all' 
    ? stake 
    : (numpadTarget ? (individualStakes[numpadTarget] || '0') : '0');

  const [systemSize, setSystemSize] = useState<number>(2);

  // Automatically adjust modes and sizes when selections count changes
  React.useEffect(() => {
    if (bets.length === 0) return;
    if (betslipMode === 'system' && bets.length < 3) {
      setBetslipMode('combi');
    }
    const maxK = bets.length - 1;
    if (systemSize < 2) {
      setSystemSize(2);
    } else if (systemSize > maxK) {
      setSystemSize(Math.max(2, maxK));
    }
  }, [bets.length, betslipMode, systemSize]);





  const hasLockedBet = React.useMemo(() => bets.some(b => b.isLocked), [bets]);

  const getIndividualStake = (betId: string) => individualStakes[betId] || stake;

  let totalStake = 0;
  if (betslipMode === 'single') {
    totalStake = bets.reduce((acc, bet) => acc + (parseFloat(getIndividualStake(bet.id)) || 0), 0);
  } else if (betslipMode === 'combi') {
    totalStake = parseFloat(stake) || 0;
  } else if (betslipMode === 'system') {
    const combCount = choose(bets.length, systemSize);
    totalStake = (parseFloat(stake) || 0) * combCount;
  }

  const stakeNum = totalStake;

  const { totalOdds: apiTotalOdds, possibleWin: apiPossibleWin, loading: quoteLoading, error: quoteError } = useBetSlipQuote(
    bets,
    betslipMode,
    stakeNum,
    systemSize,
    individualStakes
  );

  const combiOdds = bets.reduce((acc, bet) => {
    let oddVal = parseFloat(bet.odds.replace(',', '.'));
    if (isNaN(oddVal) || oddVal === 0 || bet.isLocked) oddVal = 1;
    return acc * oddVal;
  }, 1);
  const localTotalOddsStr = combiOdds.toFixed(2).replace('.', ',');
  const displayTotalOdds = (quoteLoading || quoteError || apiTotalOdds === '0,00') ? localTotalOddsStr : apiTotalOdds;

  let localPossibleWin = '0,00';
  if (betslipMode === 'single') {
    const winSum = bets.reduce((acc, bet) => {
      const itemStake = parseFloat(getIndividualStake(bet.id)) || 0;
      const itemOdds = parseFloat(bet.odds.replace(',', '.')) || 0;
      return acc + (itemStake * itemOdds);
    }, 0);
    localPossibleWin = winSum.toFixed(2).replace('.', ',');
  } else if (betslipMode === 'combi') {
    const stakeVal = parseFloat(stake) || 0;
    localPossibleWin = (stakeVal * combiOdds).toFixed(2).replace('.', ',');
  } else if (betslipMode === 'system') {
    const stakePerComb = parseFloat(stake) || 0;
    const systemWin = calculateSystemPossibleWin(bets, systemSize, stakePerComb);
    localPossibleWin = systemWin.toFixed(2).replace('.', ',');
  }

  const possibleWin = (quoteLoading || quoteError || apiPossibleWin === '0,00') ? localPossibleWin : apiPossibleWin;

  const handlePlaceBet = async () => {
    const validBets = bets.filter((b) => b.eventId && b.marketId && b.outcomeId);
    if (validBets.length === 0) {
      setBetError('Keine gültigen Auswahlen gefunden.');
      setBetStatus('error');
      return;
    }

    // Group by event for Acca/System (CustomBet support) using our centralized helper
    const event_groups = buildEventGroups(validBets);

    setPlacing(true);
    setBetStatus('idle');
    setBetError('');
    try {
      const effectiveMode = (betslipMode === 'combi' && validBets.length === 1) ? 'single' : betslipMode;
      
      if (effectiveMode === 'single') {
        // Place each single bet in parallel
        for (const bet of validBets) {
          const itemStake = betslipMode === 'combi' ? (parseFloat(stake) || 0) : (parseFloat(getIndividualStake(bet.id)) || 0);
          if (itemStake <= 0) continue;
          await placeBet({
            ticket_type: 'single',
            stake: itemStake,
            selections: [{
              event_id: bet.eventId!,
              market_id: bet.marketId!,
              outcome_id: bet.outcomeId!,
              ...(bet.specifiers ? { specifiers: bet.specifiers } : {}),
            }]
          });
        }
      } else if (effectiveMode === 'combi') {
        // Place a combi bet (acca) with event_groups
        await placeBet({ ticket_type: 'acca', stake: parseFloat(stake) || 0, event_groups });
      } else if (effectiveMode === 'system') {
        // Place a system bet natively
        const systemSelections = validBets.map(bet => ({
          event_id: bet.eventId!,
          market_id: bet.marketId!,
          outcome_id: bet.outcomeId!,
          specifiers: bet.specifiers,
        }));
        await placeBet({ 
          ticket_type: 'system', 
          stake: stakeNum, // total stake for system
          selections: systemSelections, 
          system: { combination_sizes: [systemSize] } 
        });
      }
      setBetStatus('success');
      onClearAll();
    } catch (err: any) {
      let msg = err.message || translate('slip.errorPlacingBet');
      if (msg.toLowerCase().includes('custom bet returned invalid odds') || msg.toLowerCase().includes('invalid odds')) {
        msg = translate('slip.invalidCustomBet') || "Seçtiğiniz bahisler Aynı Maç Kombinesi için birbiriyle uyumsuz (Örn: 1x2 ve Sonraki Gol). Lütfen farklı seçenekler deneyin.";
      } else if (msg.toLowerCase().includes('custombet calculation is currently unavailable')) {
        msg = "Aynı Maç Kombinesi (Bet Builder) hesaplaması şu anda sağlayıcı tarafından kullanılamıyor. Lütfen farklı maçlar seçerek kombine yapmayı deneyin.";
      } else if (msg.toLowerCase().includes('payout_limit_exceeded')) {
        msg = translate('slip.error.payoutLimitExceeded');
      }
      setBetError(msg);
      setBetStatus('error');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[150]"
        onClick={onClose}
      />

      {/* Full Screen Loading Overlay via Portal */}
      {placing && createPortal(
        <div className="fixed inset-0 bg-white/90 z-[999999] flex flex-col items-center justify-center backdrop-blur-sm">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-tipico-red border-t-transparent mb-5 shadow-sm" />
          <p className="text-[20px] font-black text-gray-800 tracking-wide uppercase">{translate('slip.placingBet')}</p>
          <p className="text-[15px] text-gray-500 mt-1 font-medium">{translate('slip.placingWait')}</p>
        </div>,
        document.body
      )}

      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 z-[160] flex flex-col max-h-[85vh] bg-white rounded-t-2xl shadow-2xl">

        {numpadTarget ? (
          <div className="flex flex-col flex-1 bg-gray-50 rounded-t-2xl overflow-hidden pb-4">
            <div className="bg-white px-5 py-6 rounded-b-2xl shadow-sm mb-2">
              <div className="flex justify-between items-center mb-1">
                <p className="text-sm font-semibold text-gray-500 tracking-wide">{translate('slip.totalStake') || 'Toplam Bahis'}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">{translate('slip.potentialWinShort')}</p>
              </div>
              <div className="flex justify-between items-end">
                <div className="text-3xl font-black text-gray-800 flex items-center">
                  {currentNumpadValue.replace('.', ',')} <span className="text-xl ml-1">€</span>
                </div>
                <div className="text-xl font-black text-green-600">
                  {possibleWin} €
                </div>
              </div>
            </div>

            <div className="flex-1 px-4 py-2 flex flex-col gap-2">
              {[ [1,2,3], [4,5,6], [7,8,9] ].map((row, i) => (
                <div key={i} className="flex gap-2">
                  {row.map(n => (
                    <button key={n} onClick={() => handleNumpadPress(n.toString())} className="flex-1 bg-white hover:bg-gray-100 rounded-xl py-3 text-2xl font-bold shadow-sm transition">{n}</button>
                  ))}
                </div>
              ))}
              <div className="flex gap-2">
                <button onClick={() => handleNumpadPress(',')} className="flex-1 bg-white hover:bg-gray-100 rounded-xl py-3 text-2xl font-bold shadow-sm transition">,</button>
                <button onClick={() => handleNumpadPress('0')} className="flex-1 bg-white hover:bg-gray-100 rounded-xl py-3 text-2xl font-bold shadow-sm transition">0</button>
                <button onClick={() => handleNumpadPress('DEL')} className="flex-1 bg-white hover:bg-gray-100 rounded-xl py-3 flex items-center justify-center shadow-sm transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 bg-transparent">
              <button
                onClick={() => setNumpadTarget(null)}
                className="w-full bg-gray-900 text-white font-bold py-4 rounded-xl text-lg hover:bg-gray-800 transition shadow-md"
              >
                {translate('slip.setStake') || 'Payı ayarla'}
              </button>
            </div>
          </div>
        ) : (
          <>
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between rounded-t-2xl" style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)' }}>
          <span className="text-white text-2xl font-bold">{translate('slip.title')}</span>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full border-2 border-white flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>


        {/* Main Tabs */}
        <div className="flex bg-white shadow-sm z-10 border-b border-gray-100">
          <div 
            onClick={() => setDrawerTab('wettschein')}
            className={`flex-1 py-3 text-center cursor-pointer transition-all ${drawerTab === 'wettschein' ? 'border-b-[3px] border-tipico-red' : 'border-b-[3px] border-transparent hover:bg-gray-50'}`}
          >
            <div className="text-[18px] font-bold text-gray-900 leading-none mb-1">{bets.length}</div>
            <div className={`text-[12px] ${drawerTab === 'wettschein' ? 'text-gray-900 font-bold' : 'text-gray-500'}`}>{translate('slip.title')}</div>
          </div>
          <div 
            onClick={() => setDrawerTab('offene')}
            className={`flex-1 py-3 text-center cursor-pointer transition-all ${drawerTab === 'offene' ? 'border-b-[3px] border-tipico-red' : 'border-b-[3px] border-transparent hover:bg-gray-50'}`}
          >
            <div className="text-[18px] font-bold text-gray-900 leading-none mb-1">{tickets.filter(t => t.status === 'open' || t.status === 'accepted').length}</div>
            <div className={`text-[12px] ${drawerTab === 'offene' ? 'text-gray-900 font-bold' : 'text-gray-500'}`}>Offene Wetten</div>
          </div>
        </div>

        {drawerTab === 'wettschein' && (
          <div className="flex flex-col flex-1 overflow-hidden">
        {/* Mode Switcher Tabs */}
        {bets.length > 0 && (
          <div className="flex border-b border-gray-100 bg-white px-2 py-2 gap-2 flex-shrink-0">
            {(['single', 'combi', 'system'] as const).map((mode) => {
              const disabled = mode === 'system' && bets.length < 3;
              const active = betslipMode === mode;
              const label = mode === 'single' ? translate('slip.single') : mode === 'combi' ? translate('slip.combi') : translate('slip.system');
              return (
                <button
                  key={mode}
                  disabled={disabled}
                  onClick={() => setBetslipMode(mode)}
                  className={`flex-1 py-2 text-center text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    active
                      ? 'bg-tipico-red text-white'
                      : 'text-gray-500 hover:text-gray-800 bg-gray-50 border border-gray-100 disabled:opacity-30 disabled:cursor-not-allowed'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 bg-gray-100 flex flex-col">
          {bets.length === 0 ? (
            betStatus === 'success' ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-white">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-5 shadow-sm">
                  <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-[18px] text-green-600 font-black mb-2 uppercase tracking-wide">{translate('slip.betSuccess')}</p>
                <p className="text-[14px] text-gray-500 font-medium">{translate('slip.betSuccessDesc')}</p>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-white">
                <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H15M9 11l3 3L22 4" />
                </svg>
                <p className="text-[15px] text-gray-400 font-medium">{translate('slip.empty')}</p>
                <p className="text-[13px] text-gray-400 font-medium mt-1">{translate('slip.emptyHint')}</p>
              </div>
            )
          ) : (
            <>
              <div className="flex flex-col bg-white border-b border-gray-200">
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-bold text-gray-800">
                    {betslipMode === 'single' ? translate('slip.single') : betslipMode === 'combi' ? translate('slip.combi') : translate('slip.system')}
                  </span>
                  <button onClick={onClearAll} className="text-gray-400 hover:text-red-500 transition">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                
                {/* System size selector for mobile */}
                {betslipMode === 'system' && (
                  <div className="px-4 pb-3 pt-0.5 flex flex-wrap items-center gap-1.5 bg-gray-50 border-t border-gray-100">
                    <span className="text-[10px] text-gray-400 font-semibold uppercase mr-1">Sistem seçin:</span>
                    {Array.from({ length: bets.length - 1 }, (_, i) => i + 2).map((k) => (
                      <button
                        key={k}
                        onClick={() => setSystemSize(k)}
                        className={`px-2 py-0.5 text-xs font-bold border rounded-md transition-all cursor-pointer ${
                          systemSize === k
                            ? 'bg-gray-800 border-gray-800 text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        {k}/{bets.length}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Bet Items */}
              {bets.map((bet) => bet.isCustomBet ? (
                /* MK+ / CustomBet item */
                <div key={bet.id} className="px-4 py-4 border-b border-gray-100 bg-white border-l-[3px] border-l-[#c8102e]">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] font-black text-white bg-[#c8102e] px-1.5 py-0.5 rounded leading-none">MK+</span>
                        <span className="text-[11px] text-gray-500 font-medium">{bet.customBetSelections?.length || 0} Auswahl</span>
                      </div>
                      <p className="text-xs text-gray-500">{bet.matchName}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-800">
                        {bet.isLocked ? (
                          <svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          bet.odds
                        )}
                      </span>
                      <button
                        onClick={() => onRemoveBet(bet.id)}
                        className="text-gray-400 hover:text-red-500 transition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Normal bet item */
                <div key={bet.id} className="px-4 py-4 border-b border-gray-100 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-3">
                      <p className="text-sm text-gray-700">
                        {bet.marketId ? resolveMarketName(bet.marketId, locale, parseSpecifiers(bet.specifiers), undefined, bet.matchName) || 'Tipp' : 'Tipp'}: <span className="font-semibold text-gray-900">{bet.marketId && bet.outcomeId ? resolveOutcomeName(bet.marketId, bet.outcomeId, locale, parseSpecifiers(bet.specifiers), bet.matchName, bet.outcomeName) : translateOutcomeLabel(bet.tippLabel, locale)}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{bet.matchName}</p>
                      {betslipMode === 'single' && (
                        <div className="mt-2.5 flex items-center gap-2">
                          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Einsatz:</span>
                          <div onClick={() => setNumpadTarget(bet.id)} className="relative w-20 cursor-pointer group">
                            <div className="w-full text-right text-xs font-bold text-gray-800 border rounded-md px-2 py-0.5 pr-5 bg-white min-h-[24px] flex items-center justify-end pointer-events-none group-hover:bg-gray-50 transition">{individualStakes[bet.id] || stake}</div>
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 pointer-events-none">€</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-800 flex items-center justify-center">
                        {bet.isLocked ? (
                          <svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          bet.odds
                        )}
                      </span>
                      <button
                        onClick={() => onRemoveBet(bet.id)}
                        className="text-gray-400 hover:text-red-500 transition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        {bets.length > 0 && (
          <div className="bg-white border-t border-gray-200 px-4 pt-4 pb-6">
            
            {/* Header row in footer: Gesamtquote / combination count */}
            <div className="px-1 pb-3 flex items-center justify-between text-xs font-semibold text-gray-500 border-b border-gray-100 mb-3.5">
              {betslipMode === 'combi' ? (
                <>
                  <span>{translate('slip.totalOdds')}:</span>
                  <span className="font-bold text-gray-800 flex items-center gap-2">
                    {displayTotalOdds}
                    {quoteLoading && <svg className="animate-spin h-3 w-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="4" className="opacity-25"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>}
                  </span>
                </>
              ) : betslipMode === 'system' ? (
                <>
                  <span>{translate('slip.combinations')}:</span>
                  <span className="font-bold text-gray-800">{choose(bets.length, systemSize)}</span>
                </>
              ) : (
                <>
                  <span>{translate('slip.singleBets')}:</span>
                  <span className="font-bold text-gray-800">{bets.length}</span>
                </>
              )}
            </div>

            {betslipMode === 'combi' ? (
              <div className="flex gap-3 mb-4">
                {/* Stake Input */}
                <div onClick={() => setNumpadTarget('combi')} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50 transition">
                  <p className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide pointer-events-none">Gesamteinsatz</p>
                  <div className="flex items-center pointer-events-none">
                    <div className="text-base font-bold text-gray-800 w-full bg-transparent min-h-[24px] flex items-center">{stake}</div>
                    <span className="text-sm font-bold text-gray-800 ml-1">€</span>
                  </div>
                </div>

                {/* Possible Win */}
                <div className="flex-1 px-3 py-2 text-right">
                  <p className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide">{translate('slip.potentialWinShort')}</p>
                  <p className="text-base font-black text-green-600">{possibleWin} €</p>
                </div>
              </div>
            ) : betslipMode === 'system' ? (
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex gap-3">
                  <div onClick={() => setNumpadTarget('system')} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50 transition">
                    <p className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide pointer-events-none">Einsatz je Weg</p>
                    <div className="flex items-center pointer-events-none">
                      <div className="text-base font-bold text-gray-800 w-full bg-transparent min-h-[24px] flex items-center">{stake}</div>
                      <span className="text-sm font-bold text-gray-800 ml-1">€</span>
                    </div>
                  </div>

                  <div className="flex-1 px-3 py-2 text-right">
                    <p className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide">{translate('slip.maxWin')}</p>
                    <p className="text-base font-black text-green-600">{possibleWin} €</p>
                  </div>
                </div>
                
                <div className="flex justify-between items-center text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                  <span>Gesamteinsatz:</span>
                  <span className="font-bold text-gray-800">{stakeNum.toFixed(2).replace('.', ',')} €</span>
                </div>
              </div>
            ) : (
              /* Single mode */
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex gap-3">
                  <div onClick={() => setNumpadTarget('all')} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50 transition">
                    <p className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide pointer-events-none">Schnelleinsatz (Alle)</p>
                    <div className="flex items-center pointer-events-none">
                      <div className="text-base font-bold text-gray-800 w-full bg-transparent min-h-[24px] flex items-center">{stake}</div>
                      <span className="text-sm font-bold text-gray-800 ml-1">€</span>
                    </div>
                  </div>

                  <div className="flex-1 px-3 py-2 text-right">
                    <p className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide">{translate('slip.potentialWinShort')}</p>
                    <p className="text-base font-black text-green-600">{possibleWin} €</p>
                  </div>
                </div>
                
                <div className="flex justify-between items-center text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                  <span>Gesamteinsatz:</span>
                  <span className="font-bold text-gray-800">{stakeNum.toFixed(2).replace('.', ',')} €</span>
                </div>
              </div>
            )}

            {hasLockedBet && (
              <p className="text-red-600 text-xs text-center font-bold mb-3 bg-red-50 py-1.5 px-2 rounded-lg border border-red-200">
                {translate('slip.hasLockedBets') || 'Ein oder mehrere Tipps sind gesperrt. Bitte entfernen.'}
              </p>
            )}



            {betStatus === 'error' && <p className="text-red-600 text-xs text-center font-bold mb-3 bg-red-50 py-2 rounded">{betError}</p>}

            {isLoggedIn ? (
              <button
                onClick={handlePlaceBet}
                disabled={placing || stakeNum <= 0 || hasLockedBet}
                className="w-full bg-tipico-red text-white font-semibold py-4 rounded-lg text-base hover:bg-tipico-red-dark transition shadow disabled:opacity-60 flex justify-center items-center cursor-pointer disabled:cursor-not-allowed"
              >
                {placing ? translate('slip.placingBtn') : translate('slip.placeBet')}
              </button>
            ) : (
              <button
                onClick={onLoginClick}
                className="w-full bg-gray-900 text-white font-semibold py-4 rounded-lg text-base hover:bg-gray-700 transition"
              >
                Anmelden
              </button>
            )}
          </div>
        )}

          </div>
        )}
        
        {drawerTab === 'offene' && (
          <div className="bg-gray-50 flex-1 overflow-y-auto p-3">
            {!isLoggedIn ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center bg-white rounded-lg border border-gray-100">
                <p className="text-gray-500 text-sm mb-4">Bitte melde dich an, um offene Wetten zu sehen.</p>
                <button onClick={onLoginClick} className="bg-tipico-red text-white px-6 py-2 rounded-lg font-bold">Anmelden</button>
              </div>
            ) : tickets.filter(t => isOpenForCashout(t) && t.ticket_type !== 'system' && t.acca_mode !== 'multi_way').length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center bg-white rounded-lg border border-gray-100">
                <span className="text-4xl mb-3">🎫</span>
                <p className="text-gray-500 text-sm">{translate('slip.noOpenBets')}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {tickets.filter(t => isOpenForCashout(t) && t.ticket_type !== 'system' && t.acca_mode !== 'multi_way').map(t => (
                  <div key={t.id} className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-bold text-gray-700">{t.ticket_type === 'system' ? 'System' : t.ticket_type === 'acca' ? translate('slip.combiBet') : translate('slip.singleBet')}</span>
                      <span className="text-xs font-bold text-gray-900">{t.total_stake} €</span>
                    </div>
                    <div className="text-[10px] text-gray-400 mb-1">ID: {t.ticket_reference || t.id}</div>
                    <div className="flex justify-between items-center text-[10px] text-gray-500 mb-3 border-b border-gray-100 pb-2">
                      <span>{translate('slip.totalOdds')}: {parseFloat(t.accepted_total_odds).toFixed(2).replace('.', ',')}</span>
                      <span className="text-green-600 font-bold">{translate('slip.potentialWinShort')}: {(t.potential_payout_is_exact === false ? '~' : '') + parseFloat(t.potential_payout).toFixed(2).replace('.', ',')} €</span>
                    </div>

                    <div className="flex flex-col gap-2 mb-2">
                      {t.legs && t.legs.map((leg, idx) => {
                        const event = t.events?.find(e => e.event_id === leg.event_id);
                        const fallbackName = eventNames[leg.event_id];
                        const eventName = (event?.event?.home?.name && event?.event?.away?.name) ? `${event.event.home.name} - ${event.event.away.name}` : fallbackName;
                        const { marketName, outcomeName } = resolveMarketAndOutcome(leg, eventName || `Event ${leg.event_id.split(':').pop()}`, locale);

                        return (
                          <div key={leg.id || idx} className="flex items-start gap-2 border-b border-dashed border-gray-100 pb-2 last:border-0 last:pb-0">
                            <div className="scale-75 origin-top-left -ml-1 mt-0.5">
                              {legStatusIcon(leg)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] font-bold text-gray-900 truncate leading-tight flex items-center gap-1.5">
                                {eventName || `Event ${leg.event_id.split(':').pop()}`}
                                {liveScores[leg.event_id] && (
                                  <span className="text-tipico-red font-black text-[10px]">
                                    [{liveScores[leg.event_id].home_score}-{liveScores[leg.event_id].away_score}]
                                    {liveScores[leg.event_id].clock && <span className="ml-0.5 text-gray-500 font-medium">({liveScores[leg.event_id].clock?.split(':')[0]}')</span>}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5 leading-tight">
                                {marketName}
                                <span className="mx-1 text-gray-300">›</span>
                                <span className="font-semibold text-gray-700">{outcomeName}</span>
                              </div>
                              {leg.result_status && leg.result_status !== 'not_resulted' && leg.result_status !== 'pending' && leg.result_status !== 'open' && leg.result_status !== 'won' && leg.result_status !== 'win' && leg.result_status !== 'lost' && leg.result_status !== 'lose' && (
                                <div className="text-[11px] text-gray-400 mt-1 bg-gray-50 px-1.5 py-0.5 rounded inline-block font-medium">
                                  {legStatusLabel(leg)}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {(() => {
                      const loading = cashoutLoading[t.id];
                      const quote = cashoutQuotes[t.id];
                      const err = cashoutErrors[t.id];
                      const confirming = cashoutConfirming[t.id];

                      if (quote && !confirming) {
                        return (
                          <div className="mt-2">
                            <button
                              disabled={loading}
                              onClick={() => setCashoutConfirming(prev => ({ ...prev, [t.id]: true }))}
                              className="w-full bg-[#00a650] hover:bg-[#009045] text-white text-[13px] font-bold py-2.5 rounded shadow-sm transition cursor-pointer flex items-center justify-center gap-2"
                            >
                              <span>Cashout</span>
                              <span>{parseFloat(quote.amount).toFixed(2).replace('.', ',')} €</span>
                            </button>
                            {err && <p className="text-[10px] text-red-500 text-center font-semibold mt-1">{err}</p>}
                          </div>
                        );
                      }

                      if (quote && confirming) {
                        return (
                          <div className="mt-3 flex flex-col gap-2">
                            <div className="flex items-center justify-between text-sm font-semibold text-gray-700">
                              <span>{translate('misc.confirmColon')}</span>
                              <span className="text-green-600 font-black text-base">{parseFloat(quote.amount).toFixed(2).replace('.', ',')} €</span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                disabled={loading}
                                onClick={() => handleConfirmCashout(t.id, quote.id)}
                                className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-bold py-2 rounded transition cursor-pointer text-center"
                              >
                                {loading ? '...' : translate('misc.confirm')}
                              </button>
                              <button
                                disabled={loading}
                                onClick={() => handleCancelCashout(t.id)}
                                className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-gray-700 font-bold py-2 rounded transition cursor-pointer text-center"
                              >
                                {translate('misc.cancel')}
                              </button>
                            </div>
                            {err && <p className="text-xs text-red-500 text-center font-semibold mt-1">{err}</p>}
                          </div>
                        );
                      }

                      return null;
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </>
  );
};


export default BettingSlipDrawer;
